// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import React, { FC, useContext } from "react";
import { css } from "styled-components";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { useActiveCartState, useCurrentCart } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import AlertTriangleLine from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleLine";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import constants from "@store/constants";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";

const enum fields {
    showTCLink = "showTCLink",
    tcLinkPreFixText = "tcLinkPreFixText",
    tcLink = "tcLink",
    tcLinkTextOverride = "tcLinkTextOverride",
    openInNewTab = "openInNewTab",
}

export interface ZeroPricingDisclaimerProps extends WidgetProps {
    orderState?: ReturnType<typeof getOrderState>;
    fields: {
        [fields.showTCLink]: boolean;
        [fields.tcLinkPreFixText]: string;
        [fields.tcLink]: LinkFieldValue;
        [fields.tcLinkTextOverride]: string;
        [fields.openInNewTab]: boolean;
    };
}

export interface ZeroPricingDisclaimerStyles {
    wrapper?: InjectableCss;
    infosBox?: InfosBoxProps;
    disclaimerText?: TypographyPresentationProps;
    termsAndConditionsLinkWrapper?: InjectableCss;
    termsAndConditionsLink?: LinkPresentationProps;
}

const zeroPricingDisclaimerStyles: ZeroPricingDisclaimerStyles = {
    wrapper: orderV2Props.sectionWrapper,
    infosBox: {
        type: "warning",
        extendedStyles: {
            icon: { src: AlertTriangleLine },
        },
    },
    disclaimerText: { forwardAs: "p" },
    termsAndConditionsLink: mergeProps<LinkPresentationProps>(linkGetActionLinkProps(ChevronRight, "right"), {
        css: css`
            font-size: inherit;
        `,
    }),
};

const styles = zeroPricingDisclaimerStyles;

const ZeroPricingDisclaimer: FC<ZeroPricingDisclaimerProps> = ({
    orderState: baseOrderState,
    fields: { showTCLink, tcLinkPreFixText, tcLink, tcLinkTextOverride, openInNewTab },
}) => {
    const { url, title: linkTitle } = useGetLink(tcLink);
    const currentPage = useCurrentPage();
    const isOrderConfirmationPage = currentPage.type === constants.pageType.orderConfirmation;
    const isCheckoutReviewAndSubmitPage = currentPage.type === constants.pageType.checkoutReviewAndSubmit;
    const orderConfirmationCartId = useAppSelector(state => state.pages.orderConfirmation.cartId);
    const cart = useActiveCartState(isOrderConfirmationPage ? orderConfirmationCartId : undefined).value;
    const orderStateContext = useContext(OrderStateContext);
    const order = (baseOrderState || orderStateContext)?.value;
    const hasZeroPriceOrderLine = order?.orderLines?.some(c => c?.totalRegularPrice === 0);
    const hasZeroPriceCartLine = cart?.cartLines?.some(c => c?.pricing?.unitNetPrice === 0);

    if (!hasZeroPriceOrderLine && !hasZeroPriceCartLine && !isCheckoutReviewAndSubmitPage && !isOrderConfirmationPage) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <InfosBox {...styles.infosBox}>
                <Typography {...styles.disclaimerText}>
                    <strong>{siteMessage("Multiple_ZeroPriceDisclaimer_Title")}</strong>
                    <br />
                    {siteMessage("Multiple_ZeroPriceDisclaimer_Description")}
                </Typography>
                {showTCLink && (
                    <StyledWrapper {...styles.termsAndConditionsLinkWrapper}>
                        {tcLinkPreFixText && <>{tcLinkPreFixText} </>}
                        <Link
                            {...styles.termsAndConditionsLink}
                            href={url}
                            target={openInNewTab ? "_blank" : undefined}
                        >
                            {tcLinkTextOverride || linkTitle}
                        </Link>
                    </StyledWrapper>
                )}
            </InfosBox>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ZeroPricingDisclaimer,
    definition: {
        displayName: "Zero Pricing Disclaimer",
        group: "Common",
        icon: "circle-i",
        fieldDefinitions: [
            {
                name: fields.showTCLink,
                displayName: "Display ",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                variant: "toggle",
                fieldType: "General",
            },
            {
                name: fields.tcLinkPreFixText,
                displayName: "Link Prefix",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: x => x.fields.showTCLink,
            },
            {
                name: fields.tcLink,
                displayName: "Terms and Conditions Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: x => false,
                allowCategories: x => false,
                isVisible: x => x.fields.showTCLink,
            },
            {
                name: fields.tcLinkTextOverride,
                displayName: "Link Title Override",
                editorTemplate: "TextField",
                defaultValue: "Terms and Conditions",
                fieldType: "Translatable",
                isVisible: x => x.fields.showTCLink,
            },
            {
                name: fields.openInNewTab,
                displayName: "Open In New Tab?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                variant: "toggle",
                fieldType: "General",
                isVisible: x => x.fields.showTCLink,
            },
        ],
        allowedContexts: ["OrderDetailsPage", "CartPage", "CheckoutReviewAndSubmitPage", "OrderConfirmationPage"],
    },
};

export default widgetModule;
